/**
 * CirculoWifi: semáforo del acceso wifi de un residente.
 *
 * Cruza los tres ejes independientes del modelo (ver
 * `docs/dev/plan-ewelink.md` § Modelo de estados):
 *   1. ¿Está autorizado?  -> accesos_wifi.estado
 *   2. ¿Está ejecutado?   -> accesos_wifi.programado_hw_at
 *   3. ¿Lo bloqueó el sistema? -> suspensiones_acceso vigente
 *
 * El círculo responde "¿está en eWeLink?"; el texto de al lado responde
 * "¿en qué parte del trámite va?". Por eso `pendiente_validacion` es gris:
 * se pidió, pero en eWeLink todavía no hay nada.
 *
 * Se reusa en la pantalla de Tesorería, en "Mis datos → Mis residentes" y en
 * la ficha de casa de Comisión.
 */

/** Estado efectivo del acceso, ya resuelto por el backend. */
export type EstadoWifi =
  | 'sin_solicitar'
  | 'en_validacion'
  | 'por_activar'
  | 'activo'
  | 'suspendido'
  | 'desactivado'
  | 'correo_cambiado'
  | 'sin_cuenta_ewelink'
  | 'acceso_perdido'
  | 'error';

const ESTADOS: Record<
  EstadoWifi,
  { color: string; texto: string; ayuda: string }
> = {
  sin_solicitar: {
    color: 'bg-text-muted/30',
    texto: 'Sin solicitar',
    ayuda: 'Este residente no tiene acceso wifi solicitado.',
  },
  en_validacion: {
    color: 'bg-text-muted',
    texto: 'En validación',
    ayuda: 'Solicitado por el vecino; la Comisión todavía no lo autoriza.',
  },
  por_activar: {
    color: 'bg-state-warning',
    texto: 'Autorizado, por activar',
    ayuda:
      'Ya fue autorizado, pero todavía no se activó en eWeLink: el portón aún no lo reconoce.',
  },
  activo: {
    color: 'bg-state-success',
    texto: 'Activo',
    ayuda: 'Activo y configurado en eWeLink.',
  },
  correo_cambiado: {
    // Ambar y no rojo: el acceso EXISTE, pero apunta al correo equivocado.
    // Rojo diria que fallo algo, y no fallo nada — hay una tarea pendiente.
    color: 'bg-state-warning',
    texto: 'Correo cambiado',
    ayuda:
      'El residente cambió su correo. El viejo sigue abriendo el portón y el nuevo no tiene acceso: hay que reprogramarlo.',
  },
  suspendido: {
    color: 'bg-state-danger',
    texto: 'Suspendido por morosidad',
    ayuda:
      'La autorización sigue vigente, pero el sistema deshabilitó el acceso por morosidad. Se reactiva solo al ponerse al día.',
  },
  desactivado: {
    color: 'bg-text-muted/30',
    texto: 'Desactivado',
    ayuda: 'El acceso fue dado de baja.',
  },
  sin_cuenta_ewelink: {
    // Ambar y no rojo: no fallo el sistema, falta un paso del vecino. Y el
    // texto es CORTO a proposito — esto se lee en el celular, donde no hay
    // espacio para una explicacion; el detalle va en el tooltip.
    color: 'bg-state-warning',
    texto: 'Falta cuenta eWeLink',
    ayuda:
      'Ese correo no tiene una cuenta de eWeLink. Se crea gratis en la aplicación eWeLink, con ese mismo correo; cuando exista, el sistema lo activa solo.',
  },
  acceso_perdido: {
    // Rojo: el vecino cree tener acceso y no le abre ninguna puerta.
    color: 'bg-state-danger',
    texto: 'Sin acceso en eWeLink',
    ayuda:
      'Se dio de alta, pero ya no está en ningún canal de eWeLink: alguien lo quitó por fuera del portal. Se resuelve con "Volver a compartir".',
  },
  error: {
    color: 'bg-state-danger',
    texto: 'Error de sincronización',
    ayuda:
      'La última operación contra eWeLink falló. Revisar en Tesorería → Acceso WiFi.',
  },
};

/** Solo el punto de color, para tablas densas. */
export function CirculoWifi({
  estado,
  className = '',
}: {
  estado: EstadoWifi;
  className?: string;
}) {
  const { color, texto, ayuda } = ESTADOS[estado];
  return (
    <span
      className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${color} ${className}`.trim()}
      role="img"
      aria-label={texto}
      title={ayuda}
    />
  );
}

/** Punto + texto, para fichas donde hay espacio. */
export function EstadoWifiInline({
  estado,
  mostrarSinSolicitar = false,
}: {
  estado: EstadoWifi;
  /** En la ficha de Comisión el wifi no se muestra si nunca se pidió. */
  mostrarSinSolicitar?: boolean;
}) {
  if (estado === 'sin_solicitar' && !mostrarSinSolicitar) return null;
  const { texto, ayuda } = ESTADOS[estado];
  return (
    <span className="inline-flex items-center gap-1.5" title={ayuda}>
      <CirculoWifi estado={estado} />
      <span className="text-xs text-text-secondary">{texto}</span>
    </span>
  );
}

export const ESTADOS_WIFI = ESTADOS;
